import React from 'react';
import AsyncSelect from 'react-select/async';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet } from '../../store/fetch';
import { openApiFetchPost } from '../../store/openApiFetch';
import { Roles } from '../../utils/enums/Roles';
import { Modal } from '../common/Modal/Modal';
import { EditPanelButton, EditPanelButtons, EditPanelSection, PanelBody, PanelButtonLink } from '../elements';
import { useUserHasRole } from '../utils/authorization/useUserHasRole';

const copySurvey = async (sourceSurveyId: string, destinationObjectId: string) => {
    const result = await openApiFetchPost('/api/surveys/{surveyId}/copyToObject', { surveyId: sourceSurveyId }, { destinationObjectId });
    return result.newSurveyId;
};

export function SurveyCopy(props: { surveyId: string }) {
    const { userHasRole } = useUserHasRole();
    const isAdmin = userHasRole([Roles.Administrator]);
    const translate = useTranslate();
    const [isObjectSelectVisible, setIsObjectSelectVisible] = React.useState(false);

    const showObjectSelect = () => {
        setIsObjectSelectVisible(true);
    };

    const copyFromObject = async (destinationObjectId: string) => {
        setIsObjectSelectVisible(false);
        await copySurvey(props.surveyId, destinationObjectId);
    };

    return (
        <>
            {isAdmin && <PanelButtonLink onClick={() => showObjectSelect()} small={true} text={translate('survey.copy')} mt icon="fa fa-copy" />}
            {isObjectSelectVisible && (
                <ObjectSelect surveyId={props.surveyId} onClose={() => setIsObjectSelectVisible(false)} onSelected={copyFromObject} />
            )}
        </>
    );
}

function ObjectSelect(props: { surveyId: string; onClose: () => void; onSelected: (objectId: string) => void }) {
    const translate = useTranslate();
    const [value, setValue] = React.useState('');

    const loadAvailableObjects = async (input?: string) => {
        let options: { value: string; label: string }[] = [];

        if (input && input.length >= 3) {
            const url = '/api/objects';
            const result = await fetchGet(url, { objectName: input }, ['NO_DATA_AVAILABLE']);
            const items = result.json.items as { id: string; name: string }[];
            options = items.map((object) => ({ value: object.id, label: object.name }));
        }
        return options;
    };

    const handleSaveObject = () => {
        if (value) {
            props.onSelected(value);
        }
    };

    const handleChange = (newValue: string) => {
        setValue(newValue);
    };

    const handleCloseFormClicked = () => {
        props.onClose();
    };

    const selectStyles = {
        menu: (styles: any) => ({ ...styles, zIndex: 999 }),
    };

    return (
        <Modal id="copy-dialog" onClose={() => handleCloseFormClicked()}>
            <Modal.Title>
                <h3>
                    <span className="panel-title">{translate('survey.copy')}</span>
                </h3>
            </Modal.Title>
            <Modal.Content style={{ height: '200px', width: '450px' }}>
                <form method="post" action="/" id="admin-form" onSubmit={() => handleSaveObject()}>
                    <div className="mb20">{translate('survey.copyExplanation')}</div>

                    <PanelBody>
                        <EditPanelSection>
                            <div style={{ width: '500px' }}>
                                <AsyncSelect
                                    id="name"
                                    name="name"
                                    backspaceRemoves={true}
                                    isClearable={true}
                                    multi={false}
                                    styles={selectStyles}
                                    inputProps={{ autoComplete: 'off', autoCorrect: 'off', spellCheck: 'off' }}
                                    loadOptions={async (inputValue: any) => loadAvailableObjects(inputValue)}
                                    onChange={(option: any) => {
                                        if (option) {
                                            handleChange(option.value);
                                        }
                                    }}
                                    valueKey="value"
                                    labelKey="label"
                                    placeholder={translate('common.searchItem', translate('common.object'))}
                                />
                            </div>
                        </EditPanelSection>
                    </PanelBody>
                </form>
            </Modal.Content>
            <Modal.Actions>
                <EditPanelButtons fixWidth={true}>
                    <EditPanelButton small={true} text={translate('common.close')} onClick={() => handleCloseFormClicked()} />
                    <EditPanelButton small={true} text={translate('common.copy')} onClick={() => handleSaveObject()} primary />
                </EditPanelButtons>
            </Modal.Actions>
        </Modal>
    );
}
